import React from 'react';
import {
  PurchaseOrder,
  PURCHASE_ORDER_STATUS_LABEL,
  STATUS_BADGE,
} from '@/types/purchase-order';

interface PurchaseOrderDetailHeaderProps {
  purchaseOrder: PurchaseOrder;
  onBack: () => void;
  actionButton?: React.ReactNode;
}

const formatTimestamp = (value?: string | null) => {
  if (!value) return '-';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  const datePart = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(parsed);

  const timePart = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(parsed);

  return `${datePart}, ${timePart.replace(':', '.')}`;
};

const PurchaseOrderDetailHeader: React.FC<PurchaseOrderDetailHeaderProps> = ({
  purchaseOrder,
  onBack,
  actionButton,
}) => {
  const hasUpdated = Boolean(
    purchaseOrder.updatedAt && purchaseOrder.updatedBy
  );
  const timestampLabel = hasUpdated ? 'Updated on' : 'Created on';
  const timestampValue = hasUpdated
    ? formatTimestamp(purchaseOrder.updatedAt)
    : formatTimestamp(purchaseOrder.createdAt);
  const actor = hasUpdated ? purchaseOrder.updatedBy : purchaseOrder.createdBy;

  return (
    <div className="mb-4">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <button
            onClick={onBack}
            className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
            aria-label="Back"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold text-gray-900">
                Purchase Order {purchaseOrder.code}
              </h1>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  STATUS_BADGE[purchaseOrder.status] ||
                  'bg-gray-100 text-gray-800'
                }`}
              >
                {PURCHASE_ORDER_STATUS_LABEL[purchaseOrder.status] ||
                  purchaseOrder.status}
              </span>
            </div>
            <p className="mt-0.5 text-xs text-gray-500">
              {timestampLabel} {timestampValue} by {actor || '-'}
            </p>
          </div>
        </div>

        {actionButton && <div className="mt-0.5">{actionButton}</div>}
      </div>
    </div>
  );
};

export default PurchaseOrderDetailHeader;
